// One-time "What's new" popup. Shows any published announcements the user has not yet seen
// (id greater than their stored lastSeenAnnId) on sign-in. Dismissing marks them all seen, so
// it never reappears for the same announcements. Suppressed while the first-run theme / palette
// choosers are up and for pending (not-yet-approved) users.

import { useState } from 'react';
import type { Announcement, AnnouncementTag } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { useSettings } from '../settings/SettingsContext.js';
import { useAnnouncementsFeed } from '../features/announcements.js';
import { renderMarkdown } from '../lib/markdown.js';
import { useFormatDate } from '../lib/format.js';

const TAG_META: Record<AnnouncementTag, { label: string; kind: string }> = {
  update: { label: 'Update', kind: 'info' },
  feature: { label: 'New', kind: 'accent' },
  fix: { label: 'Fix', kind: 'ok' },
  notice: { label: 'Notice', kind: 'warn' },
};

export function AnnouncementsModal() {
  const { me } = useAuth();
  const { prefs, needsThemeChoice, needsPaletteChoice, markAnnouncementsSeen } = useSettings();
  const { data } = useAnnouncementsFeed();
  const fmt = useFormatDate();
  const [dismissed, setDismissed] = useState(false);

  const list = data?.announcements ?? [];
  const unseen = list.filter((a) => a.id > (prefs.lastSeenAnnId ?? 0));

  // don't show for signed-out / pending users, during the first-run theme / palette choice, once
  // dismissed this session, or when there is nothing new.
  if (!me || me.user.pending || needsThemeChoice || needsPaletteChoice || dismissed || unseen.length === 0) return null;

  const dismiss = () => {
    setDismissed(true);
    markAnnouncementsSeen(Math.max(...list.map((a) => a.id)));
  };

  return (
    <div className="modal-bg" style={{ zIndex: 290 }} onClick={dismiss}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="announcements-title" style={{ width: 560, maxWidth: '94vw', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-hd">
          <h3 id="announcements-title">What's new{unseen.length > 1 ? ` · ${unseen.length} updates` : ''}</h3>
        </div>
        <div className="modal-bd" style={{ overflow: 'auto', flex: '1 1 auto', minHeight: 0, maxHeight: '70vh' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {unseen.map((a) => (
              <AnnouncementCard key={a.id} a={a} date={fmt(a.createdAt)} />
            ))}
          </div>
        </div>
        <div className="modal-ft" style={{ flex: '0 0 auto' }}>
          <button className="btn btn-primary" autoFocus onClick={dismiss}>Got it</button>
        </div>
      </div>
    </div>
  );
}

function AnnouncementCard({ a, date }: { a: Announcement; date: string }) {
  const meta = TAG_META[a.tag] ?? TAG_META.update;
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--surface)', padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <span className={`pill ${meta.kind}`}>{meta.label}</span>
        {a.version && <span className="muted" style={{ fontSize: 11, fontWeight: 600 }}>{a.version}</span>}
        <span className="spacer" style={{ flex: 1 }} />
        <span className="muted" style={{ fontSize: 11 }}>{date}</span>
      </div>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: a.body ? 4 : 0 }}>{a.title}</div>
      {a.body && (
        <div className="md-body" style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text)' }} dangerouslySetInnerHTML={{ __html: renderMarkdown(a.body) }} />
      )}
    </div>
  );
}
